import React, { useEffect, useState } from "react";
import { Dropdown, Button, Input, DatePicker } from "antd";
import {
  AddPrimaryIcon,
  CloseIcon,
  DeleteIcon,
  FilterIcon,
  IncidentIcon,
} from "../../icons";
import "./style.scss";
import Select from "../select";
import TagComp from "../tag";
import { useCallTypes } from "../../hooks/useCallTypes";
import { useUnitGroups } from "../../api/unitGroups";
import { useDepots } from "../../hooks/useDepots";

interface FilterUnitGroupProps {
  tableHeaders: { label: string; value: string }[];
  tableData: any[];
  onChange?: (filters: {
    activeFilters: string[];
    dynamicFilters: DynamicFilter[];
    apiParams: Record<string, any>;
  }) => void;
  renderValueCustom?: (
    column: string,
    filter: DynamicFilter,
    idx: number,
    updateFilter: (index: number, key: keyof DynamicFilter, val: any) => void
  ) => React.ReactNode;
  optionsByColumn?: Record<string, string[]>;
  currentPage?: number;
  perPage?: number;
  activeTab: string;
}

interface DynamicFilter {
  column: string | null;
  condition: string | null;
  value: any;
}

const conditionOptionsByColumn: Record<
  string,
  { label: string; value: string }[]
> = {
  srNo: [
    { label: "Is", value: "is" },
    { label: "Is not", value: "isNot" },
  ],
  name: [
    { label: "Is", value: "is" },
    { label: "Is not", value: "isNot" },
    { label: "Contains", value: "contain" },
    { label: "Not contains", value: "notContain" },
  ],
  callType: [
    { label: "Is", value: "is" },
    { label: "Is not", value: "isNot" },
  ],
  depotName: [
    { label: "Is", value: "is" },
    { label: "Is not", value: "isNot" },
  ],
  status: [
    { label: "Is", value: "is" },
    { label: "Is not", value: "isNot" },
  ],
  members: [
    { label: "Is", value: "is" },
    { label: "Is not", value: "isNot" },
  ],
  createdOn: [
    { label: "Is", value: "is" },
    { label: "Is not", value: "isNot" },
  ],
};

const quickFilters = [
  { key: "illegalDumping", label: "Illegal dumping", icon: <IncidentIcon /> },
  { key: "animalDead", label: "Animal dead", icon: <IncidentIcon /> },
];

// Helper function to ensure array format
const ensureArray = (val: any) => (Array.isArray(val) ? val : val ? [val] : []);

const buildApiParams = (
  activeFilters: string[],
  dynamicFilters: DynamicFilter[],
  currentPage?: number,
  perPage?: number,
  _tableData: any[] = [],
  callTypes: any[] = []
) => {
  const params: Record<string, any> = {
    paginate: true,
    page: currentPage || 1,
    per_page: perPage || 10,
  };

  const quickFilterMap: Record<string, string> = {
    illegalDumping: "Illegal dumping",
    animalDead: "Dead animal removal",
  };

  activeFilters.forEach((filterKey) => {
    const callTypeName = quickFilterMap[filterKey];
    if (!callTypeName) return;
    const svc = callTypes.find((s) => s.call_type === callTypeName);
    if (svc) {
      params["call_types_in"] = [
        ...(params["call_types_in"] || []),
        svc.service_id,
      ];
    }
  });

  // Process dynamic filters
  dynamicFilters.forEach((f) => {
    if (!f.column || !f.condition) return;

    const arrayValue = ensureArray(f.value);

    switch (f.column) {
      case "srNo":
        if (f.condition === "is" && arrayValue.length > 0) {
          params["reference_no_in"] = arrayValue
            .map((srNo: string) => {
              return srNo;
            })
            .filter(Boolean);
        }
        if (f.condition === "isNot" && arrayValue.length > 0) {
          params["reference_no_not_in"] = arrayValue
            .map((srNo: string) => {
              return srNo;
            })
            .filter(Boolean);
        }
        break;

      case "name":
        if (f.condition === "is" && arrayValue.length > 0) {
          params["name_in"] = arrayValue;
        }
        if (f.condition === "isNot" && arrayValue.length > 0) {
          params["name_not_in"] = arrayValue;
        }
        if (f.condition === "contain" && arrayValue.length > 0) {
          params["name_contain"] = arrayValue;
        }
        if (f.condition === "notContain" && arrayValue.length > 0) {
          params["name_not_contain"] = arrayValue;
        }
        break;

      case "callType":
        if (f.column === "callType" && arrayValue.length > 0) {
          const serviceIds = arrayValue
            .map((callType: string) => {
              const svc = callTypes.find((s) => s.service_id === callType);
              return svc?.service_id;
            })
            .filter(Boolean);

          if (f.condition === "is") params["call_types_in"] = serviceIds;
          if (f.condition === "isNot") params["call_types_not_in"] = serviceIds;
        }
        break;
      case "depotName":
        if (arrayValue.length > 0) {
          if (f.condition === "is") {
            params["depot_in"] = arrayValue;
          }
          if (f.condition === "isNot") {
            params["depot_not_in"] = arrayValue;
          }
        }
        break;

      case "status":
        if (f.condition === "is" && arrayValue.length > 0) {
          const statusValues = arrayValue.map((status: string) =>
            status === "Active" ? 1 : 0
          );
          params["status_in"] = statusValues;
        }
        if (f.condition === "isNot" && arrayValue.length > 0) {
          const statusValues = arrayValue.map((status: string) =>
            status === "Active" ? 1 : 0
          );
          params["status_not_in"] = statusValues;
        }
        break;

      case "members":
        if (f.condition === "is" && arrayValue.length > 0) {
          params["members_in"] = arrayValue;
        }
        if (f.condition === "isNot" && arrayValue.length > 0) {
          params["members_not_in"] = arrayValue;
        }
        break;

      case "createdOn":
        if (f.value) {
          const val = f.value;
          const dateStr =
            typeof val === "string"
              ? val
              : val?.format
              ? val.format("DD-MM-YYYY")
              : !isNaN(new Date(val).getTime())
              ? new Date(val).toISOString().split("T")[0]
              : null;

          if (!dateStr) break;

          if (f.condition === "is") {
            params["created_date_in"] = [dateStr];
          }
          if (f.condition === "isNot") {
            params["created_date_not_in"] = [dateStr];
          }
        }
        break;
    }
  });

  return params;
};

const FilterUnitGroup: React.FC<FilterUnitGroupProps> = ({
  tableHeaders,
  tableData,
  activeTab,
  onChange,
  renderValueCustom,
  // optionsByColumn,
  currentPage,
  perPage,
}) => {
  const {
    data: unitData,
    // isLoading,
    // isError,
  } = useUnitGroups({}, false);

  const { data: callTypes = [] } = useCallTypes();
  const [visible, setVisible] = useState(false);

  const [activeFilters, setActiveFilters] = useState<string[]>([]);
  const [dynamicFilters, setDynamicFilters] = useState<DynamicFilter[]>([]);

  // State for search inputs
  const [, setSrNoSearch] = useState("");
  const [, setMemberSearch] = useState("");

  // Get all unit groups data for filter options
  const allUnitGroups = unitData?.data?.unitGroups || [];

  const createDeduplicatedOptions = (
    items: any[],
    valueExtractor: (item: any) => any,
    labelExtractor: (item: any) => string = (item) =>
      valueExtractor(item)?.toString()
  ) => {
    // Count null values
    const nullCount = items.filter(
      (item) => valueExtractor(item) === null
    ).length;

    return Array.from(
      new Map(
        items.map((item) => {
          const value = valueExtractor(item);
          const key = value === null ? "null" : value;

          return [
            key,
            {
              value: value,
              label:
                value === null
                  ? `No Value (${nullCount})`
                  : labelExtractor(item) || value?.toString() || "No Value",
            },
          ];
        })
      ).values()
    );
  };

  // Prepare options from unitData
  const srNoOptions = createDeduplicatedOptions(
    allUnitGroups,
    (unit) => unit.reference_no
  );

  const nameOptions = createDeduplicatedOptions(
    allUnitGroups,
    (unit) => unit.unit_group_name,
    (unit) => unit.unit_group_name || "No Name",
    true
  );

  const callTypeOptions = createDeduplicatedOptions(
    allUnitGroups.flatMap((unit) => unit.call_types || []),
    (callType) => callType.service_id,
    (callType) => callType.service_short_description || "No Call Type",
    true
  );
  const { data: depots } = useDepots();
  let depotOptions =
    depots?.map((d) => ({ label: d.depot_name, value: d.pikitup_depot_id })) ||
    [];

  const memberOptions = createDeduplicatedOptions(
    allUnitGroups.flatMap((unit) => unit.members || []),
    (member) => member.pikitup_member_id,
    (member) => member.member_first_name || "No Name",
    false // Don't show count for members since they're less likely to have null IDs
  );

  useEffect(() => {
    const apiParams = buildApiParams(
      activeFilters,
      dynamicFilters,
      currentPage,
      perPage,
      tableData,
      callTypes
    );

    onChange?.({ activeFilters, dynamicFilters, apiParams });
  }, [activeFilters, dynamicFilters, currentPage, perPage, callTypes]);

  // ✅ Clear all filters when switching tabs
  useEffect(() => {
    setActiveFilters([]);
    setDynamicFilters([]);
  }, [activeTab]);

  const addFilter = () =>
    setDynamicFilters([
      ...dynamicFilters,
      { column: null, condition: null, value: [] },
    ]);

  const deleteFilter = (index: number) =>
    setDynamicFilters(dynamicFilters.filter((_, i) => i !== index));

  const clearAllFilters = () => {
    setActiveFilters([]);
    setDynamicFilters([]);
  };

  const toggleFilter = (key: string) =>
    setActiveFilters(
      activeFilters.includes(key)
        ? activeFilters.filter((f) => f !== key)
        : [...activeFilters, key]
    );

  const removeFilter = (key: string) =>
    setActiveFilters(activeFilters.filter((f) => f !== key));

  const updateFilter = (index: number, key: keyof DynamicFilter, val: any) => {
    const updated = [...dynamicFilters];
    updated[index][key] = val;
    setDynamicFilters(updated);
  };

  const activeFilterCount = activeFilters.length + dynamicFilters.length;

  // Render value select component
  const renderValueSelect = (f: DynamicFilter, idx: number) => {
    if (!f.column || !f.condition) return null;

    // Use custom renderer if provided
    if (renderValueCustom) {
      const custom = renderValueCustom(f.column, f, idx, updateFilter);
      if (custom) return custom;
    }

    switch (f.column) {
      case "srNo":
        return (
          <Select
            mode="multiple"
            showSearch
            allowClear
            placeholder="Search SR No"
            options={srNoOptions}
            value={f.value}
            onChange={(val) => updateFilter(idx, "value", val)}
            onSearch={setSrNoSearch}
            customWidthClass="w-full"
            className="w-full"
          />
        );

      case "callType":
        return (
          <Select
            mode="multiple"
            maxTagCount={1}
            showSearch
            allowClear
            placeholder="Select Call Type"
            options={callTypeOptions}
            value={f.value}
            onChange={(val) => updateFilter(idx, "value", val)}
            customWidthClass="w-full"
            className="w-full"
          />
        );

      case "depotName":
        return (
          <Select
            mode="multiple"
            maxTagCount={1}
            showSearch
            allowClear
            placeholder="Select Depot"
            options={depotOptions}
            value={f.value}
            onChange={(val) => updateFilter(idx, "value", val)}
            customWidthClass="w-full"
            className="w-full"
          />
        );
      case "status":
        return (
          <Select
            options={[
              { value: "Active", label: "Active" },
              { value: "Inactive", label: "Inactive" },
            ]}
            value={f.value}
            onChange={(val) => updateFilter(idx, "value", val)}
            placeholder="Select status"
            customWidthClass="w-full"
            className="w-full"
          />
        );

      case "members":
        // Get all unique members with their IDs and names
        const allMembers = Array.from(
          new Map(
            tableData.flatMap(
              (unit) =>
                unit.record?.members?.map((member: any) => [
                  member.pikitup_member_id,
                  {
                    value: member.pikitup_member_id,
                    label: member.member_first_name,
                  },
                ]) || []
            )
          ).values()
        );

        return (
          <Select
            mode="multiple"
            showSearch
            allowClear
            filterOption={false}
            placeholder="Search Members"
            options={memberOptions}
            value={f.value}
            onChange={(val) => updateFilter(idx, "value", val)}
            onSearch={setMemberSearch}
            customWidthClass="w-full"
            className="w-full"
          />
        );

      case "createdOn":
        return (
          <DatePicker
            value={f.value || null}
            onChange={(val) => updateFilter(idx, "value", val)}
            placeholder="Select date"
            className="w-full filter-date-picker"
            format="DD-MM-YYYY"
          />
        );

      default:
        return (
          <Input
            value={f.value}
            onChange={(e) => updateFilter(idx, "value", e.target.value)}
            placeholder="Enter value"
          />
        );
    }
  };

  const menu = (
    <div className="filter-dropdown">
      <div className="header-wrapper flex justify-between items-center">
        <h4 className="filter-title">Filter</h4>
        <div className="flex justify-center items-center">
          {(activeFilters.length > 0 || dynamicFilters.length > 0) && (
            <Button className="transparent clear-btn" onClick={clearAllFilters}>
              Clear all
            </Button>
          )}
          <span className="cursor-pointer" onClick={() => setVisible(false)}>
            <CloseIcon />
          </span>
        </div>
      </div>

      {/* Quick Filters */}
      <div className="quick-filter-wrapper flex flex-col">
        <span className="title">Quick filters</span>
        <div className="quick-filter flex items-center gap-3">
          {quickFilters.map((filter) =>
            activeFilters.includes(filter.key) ? (
              <TagComp
                key={filter.key}
                icon={filter.icon}
                onClose={() => removeFilter(filter.key)}
                className="ticket-type-tag quick-filter-tag"
              >
                {filter.label}
              </TagComp>
            ) : (
              <Button
                key={filter.key}
                icon={filter.icon}
                className="transparent ticket-type-btn quick-filter-btn"
                onClick={() => toggleFilter(filter.key)}
              >
                {filter.label}
              </Button>
            )
          )}
        </div>
      </div>

      {/* Custom Filters */}
      <div className="custom-filters-wrapper">
        {dynamicFilters.length > 0 && (
          <div className="dynamic-filters-wrapper">
            <span className="title">All filters</span>
            {dynamicFilters.map((f, idx) => (
              <div
                key={idx}
                className="dynamic-filter flex flex-wrap gap-2 mb-2"
              >
                {/* Column select */}
                <Select
                  className="column-filter w-full"
                  options={tableHeaders}
                  value={f.column}
                  onChange={(val) => {
                    updateFilter(idx, "column", val);
                    updateFilter(idx, "condition", null);
                    updateFilter(idx, "value", []);
                  }}
                  placeholder="Select column"
                />

                {/* Condition select */}
                {f.column && (
                  <Select
                    className="condition-filter w-full"
                    options={conditionOptionsByColumn[f.column] || []}
                    value={f.condition}
                    onChange={(val) => updateFilter(idx, "condition", val)}
                    placeholder="Condition"
                  />
                )}

                {/* Value input/select */}
                <div className="filter-value">
                  {" "}
                  {f.column && f.condition && renderValueSelect(f, idx)}
                </div>

                <Button
                  icon={<DeleteIcon />}
                  className="transparent delete-btn"
                  onClick={() => deleteFilter(idx)}
                />
              </div>
            ))}
          </div>
        )}

        <div className="footer-btn-wrapper flex items-center justify-between">
          <Button
            onClick={addFilter}
            icon={<AddPrimaryIcon />}
            className="transparent add-filter-btn"
          >
            Add Filter
          </Button>
        </div>
      </div>
    </div>
  );

  return (
    <Dropdown
      overlay={menu}
      trigger={["click"]}
      placement="bottomRight"
      arrow={false}
      overlayClassName="filter-dropdown-wrapper"
      onOpenChange={(val) => setVisible(val)}
      open={visible}
    >
      <Button
        icon={<FilterIcon />}
        className={`transparent table-filter-btn ${
          activeFilterCount > 0 ? "active-filter-btn" : ""
        }`}
      >
        {activeFilterCount > 0 && (
          <span className="filter-count">{activeFilterCount}</span>
        )}
        Filter
      </Button>
    </Dropdown>
  );
};

export default FilterUnitGroup;
